import styled from 'styled-components';
import { rgba } from 'polished';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import {
  PerpetualMarketsViewProvider,
  usePerpetualMarketsView,
} from '#/features/perpetuals/market-info/markets-context';
import { OrderFormProvider } from '#/features/perpetuals/order-builder/form-context';
import { TradesStoreProvider } from '#/features/perpetuals/trades/trades-store-context';
import {
  FutureMarketsRoutingProvider,
  useFutureMarketsRoutingView,
} from '#/features/perpetuals/markets-routing-context';
import { FuturesActiveMarketProvider } from '#/features/perpetuals/active-market-context';
import { MarketsSummaryViewProvider } from '#/features/perpetuals/market-info/markets-summary-context';
import { useLastVisitedFutureSymbolView } from '#/features/perpetuals/last-visited-future-symbol-context';
import { logException } from '#/features/logging/logging';

import {
  FUTURES_ROUTE,
  FUTURES_SYMBOL_SEGMENT,
  FuturesRouteParams,
} from '#/routes';

import { cEA135E, mainGridGapColor } from '#/colors';

import { HorizontalSpacer } from '#/components/shared/Spacer';
import { MEDIA } from '#/components/shared/media-queries';
import TradeOverview from '#/components/shared/trade-overview/TradeOverview';
import FundingPayments from '#/components/shared/trade-overview/FundingPayments';
import OpenOrders from '#/components/shared/trade-overview/OpenOrders';
import CancelAllOrders from '#/components/shared/trade-overview/CancelAllOrders';
import OpenPositions from '#/components/shared/trade-overview/OpenPositions';
import OrderHistory from '#/components/shared/trade-overview/OrderHistory';
import TabLabelFundingPayments from '#/components/shared/trade-overview/TabLabelFundingPayments';
import TabLabelOpenOrders from '#/components/shared/trade-overview/TabLabelOpenOrders';
import TabLabelOpenPositions from '#/components/shared/trade-overview/TabLabelOpenPositions';
import TabLabelOrderHistory from '#/components/shared/trade-overview/TabLabelOrderHistory';
import TabLabelTradeHistory from '#/components/shared/trade-overview/TabLabelTradeHistory';
import TradeHistory from '#/components/shared/trade-overview/TradeHistory';
import { MARKET_INFO_AREA_HEIGHT } from '#/components/shared/styles';
import { TabData } from '#/components/common/tabs';

import MarketInfo from './market-info/MarketInfo';
import OrderBookTradeTape from './order-book-trade-tape/OrderBookTradeTape';
import OrderBuilder from './order-builder/OrderBuilder';
import MarginSummary from './MarginSummary';
import TradingView from './trading-view/TradingView';
import AccountSummary from './AccountSummary';

const TABS: readonly TabData[] = [
  {
    id: 'open-positions',
    label: <TabLabelOpenPositions />,
    content: (
      <OpenPositions
        tableSpec={{
          market: { header: 'Market', minWidth: '110px' },
          side: { header: 'Side', minWidth: '40px' },
          size: { header: 'Size', minWidth: '60px' },
          notional: { header: 'Notional', minWidth: '70px' },
          entryPrice: { header: 'Entry Price', minWidth: '70px' },
          markPrice: { header: 'Mark Price', minWidth: '70px' },
          unrealizedPnl: { header: 'Unrealized P&L', minWidth: '100px' },
          fundingPnl: { header: 'Unrealized Funding', minWidth: '100px' },
          closePosition: { header: 'Close Position', minWidth: '200px' },
        }}
      />
    ),
  },
  {
    id: 'open-orders',
    label: <TabLabelOpenOrders />,
    content: (
      <OpenOrders
        tableSpec={{
          time: { header: 'Time', minWidth: '70px' },
          market: { header: 'Market', minWidth: '110px' },
          side: { header: 'Side', minWidth: '40px' },
          type: { header: 'Type', minWidth: '62px' },
          price: { header: 'Price', minWidth: '60px' },
          amount: { header: 'Amount', minWidth: '70px' },
          filled: { header: 'Filled', minWidth: '70px' },
          instruction: { header: 'Instruction', minWidth: '130px' },
          clientId: { header: 'Client Order Id', minWidth: '160px' },
          actions: { header: <CancelAllOrders />, minWidth: '80px' },
        }}
      />
    ),
  },
  {
    id: 'trade-history',
    label: <TabLabelTradeHistory />,
    content: (
      <TradeHistory
        tableSpec={{
          status: { header: 'Status', minWidth: '80px' },
          time: { header: 'Time', minWidth: '70px' },
          market: { header: 'Market', minWidth: '0px' },
          side: { header: 'Side', minWidth: '40px' },
          price: { header: 'Price', minWidth: '70px' },
          amount: { header: 'Amount', minWidth: '60px' },
          total: { header: 'Notional / Fee', minWidth: '80px' },
          liquidity: { header: 'Liquidity', minWidth: '80px' },
          txHash: { header: 'Tx Hash', minWidth: '150px' },
          id: { header: 'Id', minWidth: '70px' },
        }}
      />
    ),
  },
  {
    id: 'order-history',
    label: <TabLabelOrderHistory />,
    content: (
      <OrderHistory
        tableSpec={{
          time: { header: 'Time', minWidth: '70px' },
          market: { header: 'Market', minWidth: '110px' },
          type: { header: 'Type', minWidth: '62px' },
          side: { header: 'Side', minWidth: '40px' },
          price: { header: 'Price', minWidth: '60px' },
          amount: { header: 'Amount', minWidth: '70px' },
          remainingSize: { header: 'Remaining', minWidth: '70px' },
          status: { header: 'Status', minWidth: '64px' },
          instruction: { header: 'Instruction', minWidth: '130px' },
          cancelReason: { header: 'Cancel Reason', minWidth: '64px' },
          orderId: { header: 'Order Id', minWidth: '80px' },
          clientId: { header: 'Client Order Id', minWidth: '160px' },
        }}
      />
    ),
  },
  {
    id: 'funding-payments',
    label: <TabLabelFundingPayments />,
    content: (
      <FundingPayments
        tableSpec={{
          time: { header: 'Time', minWidth: '70px' },
          market: { header: 'Market', minWidth: '40px' },
          payment: { header: 'Payment', minWidth: '80px' },
        }}
      />
    ),
  },
];

export default function Perpetuals() {
  const { lastVisitedSymbol } = useLastVisitedFutureSymbolView();

  return (
    <FutureMarketsRoutingProvider lastVisitedSymbol={lastVisitedSymbol}>
      <RoutingRoot />
    </FutureMarketsRoutingProvider>
  );
}

function RoutingRoot() {
  const marketsRoutingView = useFutureMarketsRoutingView();

  if (marketsRoutingView.status === 'error') {
    return <ErrorMessage>{marketsRoutingView.error}</ErrorMessage>;
  }

  if (marketsRoutingView.status === 'loading') {
    return null;
  }

  if (marketsRoutingView.defaultSymbol == null) {
    logException(new Error('No default markets available'), {
      marketsRoutingView,
    });
    return <ErrorMessage>No default markets available</ErrorMessage>;
  }

  return (
    <Routes>
      <Route
        path={`${FUTURES_SYMBOL_SEGMENT}/*`}
        element={
          <SymbolRouting defaultSymbol={marketsRoutingView.defaultSymbol} />
        }
      />
      <Route
        path="*"
        element={
          <Navigate
            to={`${FUTURES_ROUTE}/${marketsRoutingView.defaultSymbol}`}
            replace
          />
        }
      />
    </Routes>
  );
}

function SymbolRouting({ defaultSymbol }: { defaultSymbol: string }) {
  const routeParams = useParams<FuturesRouteParams>();
  const marketsRoutingView = useFutureMarketsRoutingView();

  if (routeParams.symbol == null) {
    return <Navigate to={`${FUTURES_ROUTE}/${defaultSymbol}`} replace />;
  }

  if (!marketsRoutingView.hasSymbol(routeParams.symbol)) {
    return <Navigate to={`${FUTURES_ROUTE}/${defaultSymbol}`} replace />;
  }

  return <PerpetualsWithProviders symbol={routeParams.symbol} />;
}

export function PerpetualsWithProviders({ symbol }: { symbol: string }) {
  return (
    <FuturesActiveMarketProvider symbol={symbol}>
      <PerpetualMarketsViewProvider>
        <MarketsSummaryViewProvider>
          <OrderFormProvider>
            <TradesStoreProvider>
              <PerpetualsContent />
            </TradesStoreProvider>
          </OrderFormProvider>
        </MarketsSummaryViewProvider>
      </PerpetualMarketsViewProvider>
    </FuturesActiveMarketProvider>
  );
}

function PerpetualsContent() {
  const marketsView = usePerpetualMarketsView();
  return (
    <Container>
      <MarketInfo css="grid-area: market-info" />
      <LeftContainer css="grid-area: order-builder">
        <OrderBuilder />
        <HorizontalSpacer />
        <BlockContainer>
          <AccountSummary />
        </BlockContainer>
        <HorizontalSpacer />
        <BlockContainer>
          <MarginSummary />
        </BlockContainer>
      </LeftContainer>
      <TradingView
        css="grid-area: trading-view"
        symbol={marketsView.activeMarket?.symbol}
      />
      <OrderBookTradeTape css="grid-area: order-book-trade-tape" />
      <TradeOverview
        css="grid-area: trade-overview; --TABLE_ROW_HEIGHT: 40px; --MIN_VISIBLE_ROWS: 8;"
        tabData={TABS}
      />
    </Container>
  );
}

const Container = styled.div`
  flex-grow: 1;
  display: grid;

  /* 0px – md */
  grid-template-rows: ${MARKET_INFO_AREA_HEIGHT} 300px 420px 1fr 300px;
  grid-template-columns: 100vw;
  grid-template-areas:
    'market-info'
    'trading-view'
    'order-book-trade-tape'
    'order-builder'
    'trade-overview';

  @media ${MEDIA.md} {
    grid-template-rows: ${MARKET_INFO_AREA_HEIGHT} 560px 1fr;
    grid-template-columns: 1fr 300px 300px;
    grid-template-areas:
      'market-info    market-info           order-builder'
      'trading-view   order-book-trade-tape order-builder'
      'trade-overview trade-overview        order-builder';
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${mainGridGapColor};
`;

const BlockContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px;
`;

export const ErrorMessage = styled.div`
  display: grid;
  flex-grow: 1;
  place-items: center;
  color: ${rgba(cEA135E, 0.7)};
`;
